"use client";
/**
 * The catalog widget: one bounded set of movements, in whichever layout the
 * host selected.
 *
 * The three renderers are three real compositions. There is no
 * `renderMobileCard ?? renderRailCard` here and there cannot be: the kit
 * requires `renderCard` and uses it at BOTH widths, which is what stops a
 * 236px comparison card being served as a full-width standalone object. Each
 * forwards the set's open control, so the item's title — and nothing else in
 * the item — is the item's tab stop.
 *
 * The drill pane is the family's own detail in `Embedded` frame, so the pane
 * carries one bezel and one header rather than two of each.
 */
import type { CSSProperties, ReactElement, ReactNode } from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
import {
  CresultcardSet,
  CresultcardDetailFrame,
  CresultcardLayout,
  CresultcardSetStatus,
  type CresultcardOpenControl,
  type CresultcardSetEmpty,
  type CresultcardSetFailure,
} from "@/components/ui/Cresultcard";
import { cworkoutcardKey } from "./cworkoutcard.model";
import { Cworkoutcard } from "./Cworkoutcard";
import { CworkoutcardDetail } from "./CworkoutcardDetail";
import { CworkoutcardRail } from "./CworkoutcardRail";
import { CworkoutcardRow } from "./CworkoutcardRow";
import type {
  CworkoutcardData,
  CworkoutcardMoveReading,
  CworkoutcardVariant,
} from "./cworkoutcard.types";

/** Two cards side by side in the 556px column, one at Mobile's 350. */
const CARD_MIN_WIDTH = 260;
/** The drill pane arrives unpadded, so the set gives it the column's own inset. */
const DRILL_INSET = 14;
const DRILL_STYLE: CSSProperties = { minWidth: 0, padding: DRILL_INSET };

interface CworkoutcardSetProps {
  readonly exercises: readonly CworkoutcardData[];
  /**
   * A composition the host PINS for every card of this set, rather than the
   * tweak panel's current selection.
   *
   * Omitted — the dev gallery's case — the family reads the registry exactly as
   * a lone card does. Present, it is an embedder stating what this surface is
   * for: an attached result inside a deliverable leads with the demonstration
   * because the demonstration is why the row was attached, and that is not a
   * question a panel elsewhere in the app should be answering.
   */
  readonly variant?: CworkoutcardVariant;
  readonly moveReading?: CworkoutcardMoveReading;
  readonly icon: TablerIcon;
  readonly label: string;
  readonly context?: ReactNode;
  /** What bounds the whole projection. Wraps in the set header; never clipped. */
  readonly bounds?: ReactNode;
  readonly provenance?: ReactNode;
  /** The frame the drilled pane states its own values against. */
  readonly drillContext: string;
  readonly layout: CresultcardLayout;
  /**
   * The item whose drill is open when the set mounts — an attached result's
   * `full` face (results-as-resources DECISIONS §7.2). Omitted, the set mounts
   * closed. Forwarded to the kit's set untouched.
   */
  readonly initialOpenIndex?: number;
  readonly status?: CresultcardSetStatus;
  /** Required: a set that answered with nothing still has to say what it is. */
  readonly empty: CresultcardSetEmpty;
  readonly failure?: CresultcardSetFailure;
}

export function CworkoutcardSet({
  exercises,
  variant,
  moveReading,
  icon,
  label,
  context,
  bounds,
  provenance,
  drillContext,
  layout,
  initialOpenIndex,
  status = CresultcardSetStatus.Ready,
  empty,
  failure,
}: CworkoutcardSetProps): ReactElement {
  const lastIndex = exercises.length - 1;

  return (
    <CresultcardSet<CworkoutcardData>
      bounds={bounds}
      cardMin={CARD_MIN_WIDTH}
      context={context}
      empty={empty}
      failure={failure}
      icon={icon}
      items={exercises}
      initialOpenIndex={initialOpenIndex}
      keyOf={cworkoutcardKey}
      label={label}
      layout={layout}
      provenance={provenance}
      renderCard={(
        exercise: CworkoutcardData,
        _index: number,
        control: CresultcardOpenControl | undefined,
      ): ReactNode => (
        <Cworkoutcard
          data={exercise}
          moveReading={moveReading}
          openControl={control}
          variant={variant}
        />
      )}
      renderExpanded={(exercise: CworkoutcardData): ReactNode => (
        <div
          data-el="Cworkoutcard/Set/Drill"
          style={DRILL_STYLE}
        >
          <CworkoutcardDetail
            context={drillContext}
            data={exercise}
            frame={CresultcardDetailFrame.Embedded}
            moveReading={moveReading}
          />
        </div>
      )}
      renderRailCard={(
        exercise: CworkoutcardData,
        _index: number,
        control: CresultcardOpenControl | undefined,
      ): ReactNode => (
        <CworkoutcardRail data={exercise} openControl={control} />
      )}
      renderRow={(
        exercise: CworkoutcardData,
        index: number,
        control: CresultcardOpenControl | undefined,
      ): ReactNode => (
        <CworkoutcardRow
          data={exercise}
          onLast={index === lastIndex}
          openControl={control}
          rank={index + 1}
        />
      )}
      status={status}
      titleOf={(exercise: CworkoutcardData): string => exercise.title}
    />
  );
}
